// The customer portal's basket and checkout contract
// (docs/features/customer-portal-ordering/17 section 4).
//
// A separate interface from `CustomerDataAdapter`, passed to the portal as its own (optional) provider
// like the link and sign-in adapters. The real build passes `data/api/customer-basket.ts`; the staff view passes a
// read-only one; the mock twin plays the whole backend.
//
// `capabilities` is what an adapter IMPLEMENTS, one flag per group of change. Which groups are ON is the server's
// word: the basket read names an action URL only for a route that is mounted, and the portal turns a group on only
// when the read carries its URL (`domain/customer-basket-capabilities.ts`). An adapter with a group off throws
// `CustomerBasketWritesUnavailableError` from that group's methods; no page calls one.
//
// Every write posts to the URL the basket view (or an Orders row) carries and is trusted only as a relative
// path (`assertRelativeActionUrl`). The page re-reads after each write, so writes
// return nothing but the two that name something new.

import type {
  CustomerAddedBookAddress,
  CustomerAddressBook,
  CustomerAddToBasketRequest,
  CustomerArtworkSlotRef,
  CustomerBasketRefresh,
  CustomerBasketView,
  CustomerChangeLineDeliveryRequest,
  CustomerCheckoutDeliveries,
  CustomerDeliveryChoice,
  CustomerLineDeliveryChange,
  CustomerLineDeliveryPreview,
  CustomerPlaceOrderRequest,
  CustomerPlaceOrderResponse,
  CustomerSendForAPriceRequest,
  CustomerSendForAPriceResponse,
  CustomerSetBasketLineQuantityRequest,
  CustomerPostcodeLookup,
  CustomerPostcodeLookupRequest,
  CustomerTypedAddressRequest,
} from "./customer-basket-types";

/** Thrown by a change on an adapter that does not implement its group: the routes behind it are not built yet. */
export class CustomerBasketWritesUnavailableError extends Error {
  constructor(action: string) {
    super(
      `Cannot ${action}: this build of the customer basket does not make that change.`,
    );
    this.name = "CustomerBasketWritesUnavailableError";
  }
}

/**
 * The groups of change, one per set of routes, so turning one set on shows only its controls:
 * `changes` add, quantity, remove, discard and refresh (PO-3); `files` attach and detach a file check (PO-7); `checkout`
 * place the order (PO-4); `reorder` Order again (PO-5).
 */
export interface CustomerBasketCapabilities {
  readonly changes: boolean;
  readonly files: boolean;
  readonly checkout: boolean;
  readonly reorder: boolean;
}

export const NO_BASKET_CAPABILITIES: CustomerBasketCapabilities = {
  changes: false,
  files: false,
  checkout: false,
  reorder: false,
};

export const ALL_BASKET_CAPABILITIES: CustomerBasketCapabilities = {
  changes: true,
  files: true,
  checkout: true,
  reorder: true,
};

export interface CustomerBasketAdapter {
  /** Which groups of change this adapter implements; the read's URLs decide which of those are on. */
  readonly capabilities: CustomerBasketCapabilities;
  /**
   * What the page says, once, where basket changes are not on: why they are not. The shell's wording about the
   * build (coming soon, or "staff are looking at this"), not the business's. Null where the adapter implements them.
   */
  readonly readOnlyNote: string | null;
  /**
   * GET /views/customer/basket: the person's open basket at the customer they are working as (no open basket is a
   * basket with no lines). Null when the server says ordering is not open to this person yet (409
   * `ordering_needs_link_access`): the portal then shows no basket anywhere, as in a build with none.
   */
  getBasket(): Promise<CustomerBasketView | null>;
  /**
   * GET /views/customer/address-book: the person's own address book at the customer they are working as, and whether it
   * is now the only picker (`enforced`). Null when the person has no book to read (the server says ordering is not open
   * to them yet, or the staff view, which looks and does not act): the portal then offers the customer's own list as
   * before. An empty book is a book with no addresses.
   */
  getAddressBook(): Promise<CustomerAddressBook | null>;
  /**
   * POST the book's `add_url`: an address the person typed. Field refusals are 400s bound to the form's inputs. The
   * answer is the new entry, built from what was typed: the page cannot tell, and is not told, whether the address
   * already existed.
   */
  addAddressToBook(
    url: string,
    request: CustomerTypedAddressRequest,
  ): Promise<CustomerAddedBookAddress>;
  /**
   * POST the book's `postcode_lookup_url` (null where no address provider is set up, so the form offers typing only):
   * the premises at a UK postcode, in the add form's own fields. Answers the provider's addresses and nothing of the
   * customer's. A 400 names the `postcode` field; a 503 says, in words for the person, to type the address in; a
   * rejection that is not an `ApiError` is the server not being reached.
   */
  findAddressesByPostcode(
    url: string,
    request: CustomerPostcodeLookupRequest,
  ): Promise<CustomerPostcodeLookup>;
  /** POST an entry's `remove_url`: takes it out of the person's own book (the customer's address is untouched). */
  removeAddressFromBook(url: string): Promise<void>;
  /**
   * POST an entry's `update_url` to make it the person's default address (`true`: the one default at this customer, so
   * the previous one is cleared) or to clear it (`false`). The default is the picker's preselection; it is the person's
   * own, separate from the customer's default delivery address (doc 21 decision 4).
   */
  setBookAddressDefault(url: string, isDefault: boolean): Promise<void>;
  /** POST the read's `add_line_url`. Refusals bind to `delivery_address_id` and `quantity`; a moved price is a 409 `price_moved`. */
  addToBasket(url: string, request: CustomerAddToBasketRequest): Promise<void>;
  /** POST the line's `set_quantity_url`. */
  setBasketLineQuantity(
    url: string,
    request: CustomerSetBasketLineQuantityRequest,
  ): Promise<void>;
  /**
   * POST the line's `preview_delivery_url` (named only while `customer_delivery_dates` is on): the dates the line could
   * arrive on at the address or split in `change`, each with the whole line's price, and the delivery at the date
   * selected. Nothing is stored. Part of the `changes` group.
   */
  previewBasketLineDelivery(
    url: string,
    change: CustomerLineDeliveryChange,
  ): Promise<CustomerLineDeliveryPreview>;
  /**
   * POST the line's `change_delivery_url`: moves the line to another date, address or split, kept only at
   * `expected_price_pence`, the price the preview showed. A price that moved is a 409 `price_moved` carrying
   * `new_price_pence` and nothing is changed; a date no longer offered is a 400 on `speed_code`. The page reads the
   * basket again.
   */
  changeBasketLineDelivery(
    url: string,
    request: CustomerChangeLineDeliveryRequest,
  ): Promise<void>;
  /** POST the line's `remove_url`. */
  removeBasketLine(url: string): Promise<void>;
  /** POST a slot's `attach_url` (the line's, shared by its slots): puts a file check from the portal's artwork check on that slot. */
  attachArtworkCheck(
    url: string,
    slot: CustomerArtworkSlotRef,
    checkId: string,
  ): Promise<void>;
  /** POST a slot's `detach_url`. */
  detachArtworkCheck(url: string, slot: CustomerArtworkSlotRef): Promise<void>;
  /** POST the basket's `discard_url`. */
  discardBasket(url: string): Promise<void>;
  /**
   * POST the basket's `refresh_url` (named only while `customer_delivery_dates` is on): prices again the lines the read
   * flags `needs_refresh`, a few at a time within a deadline. A line it cannot confirm now is left as stored and named
   * in `to_confirm_line_ids`: that is an answer, not a failure. The page calls it once on arrival and reads the basket
   * again.
   */
  refreshBasket(url: string): Promise<CustomerBasketRefresh>;
  /**
   * POST the basket's `preview_deliveries_url`: the checkout's Delivery section. Each delivery the order will make
   * under `choices` (the levels at their price and arrival, or collected, included, not yet priced), the collection
   * points, and the totals with delivery. Sent again with every current choice each time one changes; nothing is
   * stored. Part of the `checkout` group.
   */
  previewCheckoutDeliveries(
    url: string,
    choices: CustomerDeliveryChoice[],
  ): Promise<CustomerCheckoutDeliveries>;
  /**
   * POST the basket's `place_order_url`, with the Delivery section's choices. Field refusals are 400 (an order with a
   * delivery not yet priced is refused, code `delivery_cannot_be_priced`: only staff agree a price); prices that
   * moved are a 409 `prices_changed`. The answer names where to go: the card page when there is a `checkout_url`,
   * then (or at once) `next_path`, the order's page.
   */
  placeOrder(
    url: string,
    request: CustomerPlaceOrderRequest,
  ): Promise<CustomerPlaceOrderResponse>;
  /** POST an Orders row's `reorder_url`: rebuilds its lines in the person's basket, priced today. */
  reorderOrder(url: string): Promise<void>;
  /**
   * POST the basket's `send_for_a_price_url` (set while it holds a quote request, spec 32 §5.7): the whole basket
   * becomes one request our team prices. A basket with nothing to price by hand is a 409
   * `basket_has_no_quote_request`. Part of the `checkout` group.
   */
  sendBasketForAPrice(
    url: string,
    request: CustomerSendForAPriceRequest,
  ): Promise<CustomerSendForAPriceResponse>;
}
